import DotMark16 from '@carbon/icons-react/es/dot-mark/16';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { FC, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Caption01 } from 'components/styles';
import { formatTime } from './format-time';

const getChartOptions = (
  billablePercent,
  nonBillablePercent,
  nonTrackedPercent,
) => ({
  chart: {
    plotShadow: false,
    plotBackgroundColor: null,
    plotBorderWidth: null,
    width: 56,
    height: 56,
  },
  title: {
    text: '',
  },
  plotOptions: {
    pie: {
      dataLabels: {
        enabled: false,
      },
      enableMouseTracking: false,
      center: ['50%', -2],
      size: 56,
      states: {
        hover: {
          halo: null,
        },
      },
      animation: {
        defer: 500,
      },
    },
  },
  tooltip: {
    enabled: false,
  },
  series: [
    {
      type: 'pie',
      data: [
        {
          y: billablePercent,
          color: 'var(--green-40)',
        },
        {
          y: nonBillablePercent,
          color: 'var(--purple-40)',
        },
        {
          y: nonTrackedPercent,
          color: 'var(--coolest-gray-40)',
        },
      ],
    },
  ],
});

const CircularBar = ({
  billablePercent,
  nonBillablePercent,
  nonTrackedPercent,
}: {
  billablePercent: number;
  nonBillablePercent: number;
  nonTrackedPercent: number;
}) => (
  <HighchartsReact
    highcharts={Highcharts}
    options={getChartOptions(
      billablePercent,
      nonBillablePercent,
      nonTrackedPercent,
    )}
  />
);

const LegendWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const Label = styled.div`
  ${Caption01};
  color: #000;
  margin-left: var(--spacing-03);
  display: flex;
  align-items: center;

  svg {
    width: 6px;
    height: 6px;
    margin: 0 4px;
  }
`;
const LegendIcon = styled.div<{ color }>`
  width: 6px;
  height: 6px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
`;

const Legend = ({
  color,
  value,
  title,
}: {
  color: string;
  value: ReactNode;
  title: string | ReactNode;
}) => (
  <LegendWrapper>
    <LegendIcon color={color} />
    <Label>
      {value} <DotMark16 />
      {title}
    </Label>
  </LegendWrapper>
);

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Legends = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: var(--spacing-05);
  gap: 4px;
`;

interface Props {
  billableMinutes: number;
  nonBillableMinutes: number;
  capacityInMinutes?: number;
  className?: string;
}

const calculatePercentage = (value = 0, baseValue = 0): number =>
  baseValue === 0 ? 0 : (value / baseValue) * 100;

export const BillabliltyChart: FC<Props> = ({
  billableMinutes,
  nonBillableMinutes,
  capacityInMinutes,
  className,
}) => {
  const totalMinutes = useMemo(
    () => billableMinutes + nonBillableMinutes,
    [billableMinutes, nonBillableMinutes],
  );
  const billablePercent = useMemo(
    () =>
      Math.round(
        calculatePercentage(billableMinutes, capacityInMinutes ?? totalMinutes),
      ),
    [billableMinutes, capacityInMinutes, totalMinutes],
  );
  const nonBillablePercent = useMemo(
    () =>
      Math.round(
        calculatePercentage(
          nonBillableMinutes,
          capacityInMinutes ?? totalMinutes,
        ),
      ),
    [nonBillableMinutes, capacityInMinutes, totalMinutes],
  );
  const notTrackedMinutes =
    capacityInMinutes != null && totalMinutes < capacityInMinutes
      ? capacityInMinutes - totalMinutes
      : 0;
  const notTrackedPercent = useMemo(
    () =>
      capacityInMinutes != null && totalMinutes < capacityInMinutes
        ? Math.round(
            calculatePercentage(
              capacityInMinutes - totalMinutes,
              capacityInMinutes,
            ),
          )
        : 0,
    [totalMinutes, capacityInMinutes],
  );

  return (
    <Wrapper className={className}>
      <CircularBar
        billablePercent={billablePercent}
        nonBillablePercent={nonBillablePercent}
        nonTrackedPercent={notTrackedPercent}
      />
      <Legends>
        <Legend
          color="var(--green-40)"
          title={<FormattedMessage id="timesheets.column.billable" />}
          value={formatTime(billableMinutes)}
        />
        <Legend
          color="var(--purple-40)"
          title={<FormattedMessage id="timesheets.column.non_billable" />}
          value={formatTime(nonBillableMinutes)}
        />
        {capacityInMinutes != null ? (
          <Legend
            color="var(--coolest-gray-40)"
            title={<FormattedMessage id="timesheets.labels.not_tracked" />}
            value={formatTime(notTrackedMinutes)}
          />
        ) : null}
      </Legends>
    </Wrapper>
  );
};
